import React, { useMemo } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import { usePersistedDataStore } from '../../../store';
import { getConversionFactorForNegotiationParcel } from '../../../../utils/conversionUtils';
import logger from '../../../../utils/logger';

const OfferRoundConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  onEditPriorRound,
  parcelId,
  newRoundNumber,
  negotiationRounds = [],
  offeredBy,
  newOfferValue
}) => {
  const negotiationObj = usePersistedDataStore((state) => state.negotiationObj);

  // Only resolve case/EQ/RAR while open — parent keeps this mounted with open=false and cleared data.
  const conversionFactor = useMemo(() => {
    if (!open || parcelId == null || parcelId === '') return null;
    return getConversionFactorForNegotiationParcel(negotiationObj, parcelId);
  }, [open, negotiationObj, parcelId]);

  if (!open || conversionFactor == null) return null;

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return 'N/A';
    }
    // Convert AV value to MV for display (divide by conversion factor)
    const marketValue = Number(value) / conversionFactor;
    const avValue = Number(value);
    return `$${Math.round(marketValue).toLocaleString()} (${avValue.toLocaleString()} AV)`;
  };

  const getPartyColor = (party) => {
    return party === 'Aventine' ? '#1976d2' : '#f57c00';
  };

  const getPartyChipColor = (party) => {
    return party === 'Aventine' ? 'primary' : 'warning';
  };

  return (
    <StableMuiDialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          New Offer Round Confirmation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This update will create a new offer round #{newRoundNumber}. Is this intentional, or did you want to update the previous round's offer?
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            New Offer Details:
          </Typography>
          <Box sx={{ 
            p: 2, 
            border: '2px solid', 
            borderColor: getPartyColor(offeredBy),
            borderRadius: 1,
            backgroundColor: `${getPartyColor(offeredBy)}10`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip 
                label={offeredBy} 
                color={getPartyChipColor(offeredBy)}
                size="small"
              />
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Round #{newRoundNumber}
              </Typography>
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: formatCurrency(newOfferValue) === 'N/A' ? 'text.secondary' : getPartyColor(offeredBy)
              }}
            >
              {formatCurrency(newOfferValue)}
            </Typography>
          </Box>
        </Box>

        {negotiationRounds.length > 0 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Previous Offer Rounds:
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Round</TableCell>
                    <TableCell align="center">Aventine Offer</TableCell>
                    <TableCell align="center">Town Offer</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {negotiationRounds
                    .sort((a, b) => (b.round || 0) - (a.round || 0))
                    .map((round, index) => {
                      const aventineOffer = round.offers?.find(o => o.OfferedBy === 'Aventine');
                      const townOffer = round.offers?.find(o => o.OfferedBy === 'Town');
                      
                      // DEBUG: Log offer values in dialog
                      logger.log(`🔍 [DIALOG DEBUG] Round ${round.round}:`, {
                        aventineOffer: aventineOffer ? {
                          Offer: aventineOffer.Offer,
                          OfferID: aventineOffer.OfferID,
                          OfferedBy: aventineOffer.OfferedBy
                        } : null,
                        townOffer: townOffer ? {
                          Offer: townOffer.Offer,
                          OfferID: townOffer.OfferID,
                          OfferedBy: townOffer.OfferedBy
                        } : null
                      });
                      
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              #{round.round || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {aventineOffer ? (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 'bold',
                                  color: formatCurrency(aventineOffer.Offer) === 'N/A' ? 'text.secondary' : 'inherit'
                                }}
                              >
                                {formatCurrency(aventineOffer.Offer)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No offer
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {townOffer ? (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 'bold',
                                  color: formatCurrency(townOffer.Offer) === 'N/A' ? 'text.secondary' : 'inherit'
                                }}
                              >
                                {formatCurrency(townOffer.Offer)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No offer
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onEditPriorRound?.(round.round || 1)}
                              sx={{ minWidth: 'auto', px: 1 }}
                            >
                              Edit Round #{round.round || 'N/A'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={onCancel} 
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained"
          color="primary"
        >
          Create New Round #{newRoundNumber}
        </Button>
      </DialogActions>
    </StableMuiDialog>
  );
};

export default OfferRoundConfirmationDialog;
